<template>
  <div class="process-lineage-view">
    <div role="tab">
      <div role="button" class="el-collapse-item__header is-active">PROCESS LINEAGE</div>
    </div>
    <div class="process-lineage-details flex box grow" v-loading="processLineageReqStatus === 'loading'">
      <div class="process-lineage-graph box" v-if="!$_.isEmpty(selectedProcess)">
        <el-tooltip class="item" effect="dark" content="Sort By" placement="top" :enterable="false">
          <span class="body2">
            <el-select
              v-model="sortSel"
              @change="sortProcessLineageList(sortSel)"
              placeholder="Select"
              class="body2"
              style="max-width: 125px"
            >
              <el-option v-for="item in sortOptions" :key="item.value" :label="item.label" :value="item.value">
                <span class="body2">{{ item.label }}</span>
              </el-option>
            </el-select>
          </span>
        </el-tooltip>
        <div class="ioc-summary" v-if="sortSel === 'ascending'">
          <span class="start-circle">
            <p v-if="operatingSystem === 'linux'">init</p>
            <p v-else class="smaller-font">wininit</p>
          </span>
        </div>
        <div
          class="ioc-summary cursor-pointer"
          v-for="(process, idx) in ioc.processLineage.processLineageEvents"
          :key="idx"
          @click="
            selectedProcess = process
            selectedIndex = idx
          "
        >
          <span class="ioc-summary_details box flex" :class="{ 'active-process': selectedIndex === idx }">
            <span class="box ioc-point display-inline-block">
              <span class="material-icons">device_hub</span>
            </span>
            <div class="box flex column ioc-details">
              <p class="box body1 display-inline">{{ process.comm }}</p>
              <span class="box ioc-details_time">
                <time class="display-inline-block caption1">{{ getDateTimeFormat(process.starttime) }}</time>
              </span>
            </div>
          </span>
        </div>
        <div class="ioc-summary" v-if="sortSel === 'descending'">
          <span class="start-circle">
            <p v-if="operatingSystem === 'linux'">init</p>
            <p v-else class="smaller-font">wininit</p>
          </span>
        </div>
      </div>
      <div class="process-lineage-info box" v-if="!$_.isEmpty(selectedProcess)">
        <h3>{{ selectedProcess.comm }}</h3>
        <div
          v-for="keyVal in displayKeyValue"
          :key="keyVal.key"
          :class="{
            'display-none': $_.isEmpty(selectedProcess[keyVal.key]) && !$_.isNumber(selectedProcess[keyVal.key])
          }"
        >
          <span class="subtitle2">{{ keyVal.title }}</span>
          <span class="value" v-if="keyVal.key !== 'starttime'">{{ selectedProcess[keyVal.key] }}</span>
          <time class="value" v-else>{{ getDateTimeFormat(selectedProcess[keyVal.key]) }}</time>
        </div>
        <div
          v-for="userKeyVal in userKeyValue"
          :key="userKeyVal.key"
          :class="{
            'display-none': $_.isEmpty(selectedProcess[userKeyVal.key]) && !$_.isNumber(selectedProcess[userKeyVal.key])
          }"
        >
          <span class="subtitle2">{{ userKeyVal.title }}</span>
          <span class="value">{{ selectedProcess[userKeyVal.key] }}</span>
        </div>
      </div>
      <div class="box grow no-data" v-if="processLineageReqStatus === 'saved' && $_.isEmpty(selectedProcess)">
        <h3>Process Lineage data is unavailable.</h3>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { OrderByEnum } from '@/constants/orderby.constants'
import { NANOSECONDS_TO_MILLISECONDS, DATETIME_FORMAT } from '@/constants/datetime.constants'
import { AGENT_LIST_REQ } from '@/store/modules/agents/agents.type'
import { PROCESS_LINEAGE_REQ } from '@/store/modules/process_lineage/process-lineage.type'

export default {
  name: 'ProcessLineageGraph',
  props: {
    ioc: { type: Object }
  },
  data () {
    return {
      operatingSystem: 'linux',
      sortSel: OrderByEnum.ASC, // default sort
      sortOptions: [
        {
          value: OrderByEnum.DESC,
          label: 'Newest First'
        },
        {
          value: OrderByEnum.ASC,
          label: 'Oldest First'
        }
      ],
      selectedProcess: {},
      selectedIndex: 0,
      userKeyValue: [],
      displayKeyValue: [
        {
          title: 'Start Time',
          key: 'starttime'
        },
        {
          title: 'Pid',
          key: 'pid'
        },
        {
          title: 'Parent pid',
          key: 'ppid'
        },
        {
          title: 'Binary path',
          key: 'filename'
        },
        {
          title: 'Command Line',
          key: 'flattenedArgv'
        }
      ],
      linuxUserKeyValue: [
        {
          title: 'User ID',
          key: 'uid'
        },
        {
          title: 'Effective User ID',
          key: 'euid'
        },
        {
          title: 'Session ID',
          key: 'sessionid'
        }
      ],
      windowsUserKeyValue: [
        {
          title: 'SID',
          key: 'sid'
        },
        {
          title: 'User Name',
          key: 'logonUser'
        },
        {
          title: 'Login ID',
          key: 'logonId'
        }
      ]
    }
  },
  methods: {
    getDateTimeFormat (tmstp) {
      return this.$moment(tmstp * NANOSECONDS_TO_MILLISECONDS).format(DATETIME_FORMAT)
    },
    sortProcessLineageList (sortBy) {
      // only called on sort change (different sort selected), safe to always reverse
      this.ioc.processLineage.reverse()
    },
    setUserKeyValue () {
      if (this.ioc.agentDetails.system && this.ioc.agentDetails.system.os === 'windows') {
        this.operatingSystem = 'windows'
        this.userKeyValue = this.windowsUserKeyValue
      } else {
        this.userKeyValue = this.linuxUserKeyValue
      }
    },
    getProcessLineageDetails () {
      this.$store
        .dispatch(PROCESS_LINEAGE_REQ, {
          agentId: this.ioc.agentId,
          trailId: this.ioc.trailId,
          iocHash: this.ioc.iocHash
        })
        .then(processLineage => {
          this.ioc.agentDetails = _.find(this.agentList.models, { agentId: this.ioc.agentId }) || {}
          this.setUserKeyValue()
          this.ioc.processLineage = processLineage
          if (!_.isEmpty(this.ioc.processLineage.processLineageEvents)) {
            this.selectedProcess = this.ioc.processLineage.processLineageEvents[0]
          }
        })
    }
  },
  created () {
    if (_.isEmpty(this.ioc)) {
      return false
    }
    if (this.agentListReqStatus !== 'saved') {
      this.$store.dispatch(AGENT_LIST_REQ).then(resp => {
        this.getProcessLineageDetails()
      })
    } else if (!this.ioc.processLineage) {
      this.getProcessLineageDetails()
    } else {
      this.setUserKeyValue()
      if (!_.isEmpty(this.ioc.processLineage.processLineageEvents)) {
        this.selectedProcess = this.ioc.processLineage.processLineageEvents[0]
      }
    }
  },
  computed: {
    ...mapGetters(['agentListReqStatus', 'agentList', 'processLineageReqStatus'])
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/icon.css';

.process-lineage-view {
  height: calc(100%-190px);
}

.process-lineage-details {
  height: 100%;

  > .box {
    width: 50%;
    height: inherit;
    overflow-y: scroll;
  }
}

>>> .el-collapse-item__header {
  height: 40px;
  line-height: 40px;
  background-color: var(--gray-50);
  border-bottom: 1px solid var(--border-color);
  padding-left: 15px;
  font-size: 10px;
  font-weight: normal;
  color: inherit;
  line-height: 32px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.ioc-summary {
  margin: 0px 15%;

  &:first-of-type {
    padding-top: 37px;
  }

  &:not(:last-of-type)::after {
    content: '';
    position: relative;
    border-left: 1px solid var(--aluminum-600);
    height: 50px;
    top: 0px;
    left: 25px;
    z-index: 0;
  }
}

.ioc-summary_details {
  z-index: 99;
  position: relative;
  padding: 8px 10px 8px 6px;
  background-color: var(--denim-50);
  margin: 0px;
  border: 1px solid var(--aluminum-600);
  border-bottom-left-radius: 140px;
  border-top-left-radius: 140px;
  max-width: 350px;

  &.active-process {
    background-color: var(--denim-100);
  }

  p {
    margin: 0px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ioc-details {
    margin-left: 16px;
    visibility: visible;
    transition: width 1s visibility 0.5s ease-in-out;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .ioc-details_time {
      max-height: 15px;

      time,
      p {
        vertical-align: top;
      }
    }
  }

  &.details-hidden {
    max-width: 0px;
    background-color: transparent;
    border: 0px;
    border-bottom-left-radius: 0px;
    border-top-left-radius: 0px;

    .ioc-details {
      visibility: hidden;
    }

    .ioc-occurrence {
      left: 150%;
      top: 65%;
    }
  }

  .ioc-point {
    min-width: 32px;
    min-height: 32px;
    line-height: 32px;
    border: 1px solid var(--aluminum-600);
    border-radius: 50%;
    background-color: white;
    text-align: center;

    .material-icons {
      vertical-align: middle;
    }
  }
}

.process-lineage-graph {
  .el-tooltip {
    display: block;
    padding-bottom: 1.5em;
  }

  .el-select {
    float: right;
  }

  >>> .el-input__inner {
    border: none;
  }

  .start-circle {
    padding-left: 8px;
    display: block;

    p {
      width: 32px;
      height: 32px;
      line-height: 32px;
      border: 1px solid var(--aluminum-600);
      border-radius: 50%;
      background-color: white;
      font-size: 0.8em;
      text-align: center;
      margin: 0px;
    }
  }
}

.process-lineage-info {
  border-left: 1px solid var(--border-color);

  h3 {
    padding: 10px;
    border-bottom: 1px solid var(--border-color);
  }

  div {
    display: flex;
    flex-wrap: nowrap;
    padding: 5px;

    > .subtitle2,
    .value {
      flex-grow: 1;
      align-self: auto;
      max-width: 50%;
    }
  }
}
.smaller-font {
  font-size: 0.65em !important;
}

.no-data {
  h3 {
    line-height: 20em;
    vertical-align: middle;
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/graph/ProcessLineageGraph.vue